import type { Router } from 'vue-router';

import { useErrorStore } from '../stores/error';
import type { ErrorState } from '../stores/types';

/**
 * Registers error-clearing middleware on the router.
 *
 * - beforeEach: snapshots the current error reference
 * - afterEach: clears the error only if it's the same reference (stale from previous page).
 *   If a guard (e.g. permission middleware) called setError during navigation,
 *   the reference will differ and afterEach leaves it intact.
 *
 * Must be registered BEFORE other beforeEach guards so the snapshot is taken first.
 *
 * @param router - Vue Router instance
 *
 * @example
 * ```typescript
 * // In router/index.ts
 * import { registerErrorMiddleware } from '@hive/core';
 *
 * const router = createRouter({ ... });
 *
 * // Register BEFORE other guards
 * registerErrorMiddleware(router);
 * router.beforeEach(authMiddleware);
 * router.beforeEach(permissionMiddleware);
 *
 * export default router;
 * ```
 */
export function registerErrorMiddleware(router: Router): void {
  let errorBeforeNavigation: ErrorState | null = null;

  router.beforeEach(() => {
    const errorStore = useErrorStore();
    errorBeforeNavigation = errorStore.error;
  });

  router.afterEach(() => {
    const errorStore = useErrorStore();

    // Only clear if the error is the same object from before navigation started.
    // If a guard set a new error, the reference will differ — leave it intact.
    if (errorStore.error && !errorStore.error.critical && errorStore.error === errorBeforeNavigation) {
      errorStore.clearError();
    }
  });
}
